import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import config from '../config'; // Update this path to where your config file is located

// ==========================================
// REDUX THUNKS (ASYNC API CALLS)
// ==========================================

// 1. Fetch Users for Bulk Edit
// 1. Fetch Users for Bulk Edit
export const fetchBulkUsersList = createAsyncThunk(
  'allUsers/fetchBulkUsersList',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      const response = await axios.get(`${config.apiURL}/bulkEditUsersList`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // DEBUG: Look at your console to see exactly what the backend sends
      console.log("USERS API RESPONSE:", response.data);

      const responseData = response.data?.data;

      // 1. If it's a standard array, return it
      if (Array.isArray(responseData)) return responseData;

      // 2. If SQL Server wrapped it in 'recordset', return the recordset array
      if (responseData?.recordset && Array.isArray(responseData.recordset)) {
        return responseData.recordset;
      }

      return [];
    } catch (error) {
      console.error('Error fetching bulk users:', error);
      return rejectWithValue(error.response?.data || 'Failed to fetch bulk users');
    }
  }
);

// 2. Fetch Reports for Bulk Edit
export const fetchBulkReportsList = createAsyncThunk(
  'allUsers/fetchBulkReportsList',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      const response = await axios.get(`${config.apiURL}/bulkEditReportsList`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // DEBUG: Look at your console to see exactly what the backend sends
      console.log("REPORTS API RESPONSE:", response.data);

      const responseData = response.data?.data;

      if (Array.isArray(responseData)) return responseData;
      if (responseData?.recordset && Array.isArray(responseData.recordset)) {
        return responseData.recordset;
      }

      return [];
    } catch (error) {
      console.error('Error fetching bulk reports:', error);
      return rejectWithValue(error.response?.data || 'Failed to fetch bulk reports');
    }
  }
);

// 3. Post Payload to Add Reports to Users
export const addBulkUserReportsMapping = createAsyncThunk(
  'allUsers/addBulkUserReportsMapping',
  async ({ payload, onSuccess }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      const response = await axios.post(`${config.apiURL}/bulkUserToReportsMapping`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (onSuccess) onSuccess(response.data?.message || 'Reports added successfully!');
      return response.data;
    } catch (error) {
      console.error('Error adding mapping:', error);
      return rejectWithValue(error.response?.data || 'Failed to add mapping');
    }
  }
);

// 4. Post Payload to Delete Reports from Users
export const deleteBulkReportsMapping = createAsyncThunk(
  'allUsers/deleteBulkReportsMapping',
  async ({ payload, onSuccess }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      const response = await axios.post(`${config.apiURL}/bulkDeleteUserToReportsMapping`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (onSuccess) onSuccess(response.data?.message || 'Reports removed successfully!');
      return response.data;
    } catch (error) {
      console.error('Error deleting mapping:', error);
      return rejectWithValue(error.response?.data || 'Failed to delete mapping');
    }
  }
);


// ==========================================
// SLICE
// ==========================================

const adminReducer = createSlice({
    name: 'allUsers',
    initialState: {
        usersDetails: [],
        userDetailsByAudience: [],
        audienceList: [],
        audienceReport: [],
        allReports: [],
        allReportGroups: [],
        allUsersByReport: [],
        reportsForUser: {},
        isLoading: false,
        isReportsLoading: false,
        rolesAssignedToUser: [],

        // --- NEW STATE FOR BULK EDIT ---
        bulkUsersList: [],
        bulkReportsList: [],
        isBulkLoading: false,
    },
    reducers: {
        addAllUsersByReport(state, action) { state.allUsersByReport = action.payload; },
        getUsers(state, action) { state.usersDetails = action.payload; },
        getUsersByAudience(state, action) { state.userDetailsByAudience = action.payload; },
        getAudience(state, action) { state.audienceList = action.payload; },
        getAudienceReport(state, action) { state.audienceReport = action.payload; },
        getReportsReducer(state, action) { state.allReports = action.payload; },
        getAllGroupsReducer(state, action) { state.allReportGroups = action.payload; },
        getReportsByUser(state, action) { state.reportsForUser = action.payload; },
        addLoading(state, action) { state.isLoading = action.payload; },
        addReportsLoading(state, action) { state.isReportsLoading = action.payload; },
        addRolesAssignedToUser(state, action) { state.rolesAssignedToUser = action.payload; },
    },
    extraReducers: (builder) => {
        builder
            // --- Fetch Bulk Users ---
            .addCase(fetchBulkUsersList.pending, (state) => {
                state.isBulkLoading = true;
            })
            .addCase(fetchBulkUsersList.fulfilled, (state, action) => {
                state.isBulkLoading = false;
                state.bulkUsersList = action.payload;
            })
            .addCase(fetchBulkUsersList.rejected, (state) => {
                state.isBulkLoading = false;
            })

            // --- Fetch Bulk Reports ---
            .addCase(fetchBulkReportsList.pending, (state) => {
                state.isBulkLoading = true;
            })
            .addCase(fetchBulkReportsList.fulfilled, (state, action) => {
                state.isBulkLoading = false;
                state.bulkReportsList = action.payload;
            })
            .addCase(fetchBulkReportsList.rejected, (state) => {
                state.isBulkLoading = false;
            })

            // --- Add Mapping ---
            .addCase(addBulkUserReportsMapping.pending, (state) => {
                state.isBulkLoading = true;
            })
            .addCase(addBulkUserReportsMapping.fulfilled, (state) => {
                state.isBulkLoading = false;
            })
            .addCase(addBulkUserReportsMapping.rejected, (state) => {
                state.isBulkLoading = false;
            })

            // --- Delete Mapping ---
            .addCase(deleteBulkReportsMapping.pending, (state) => {
                state.isBulkLoading = true;
            })
            .addCase(deleteBulkReportsMapping.fulfilled, (state) => {
                state.isBulkLoading = false;
            })
            .addCase(deleteBulkReportsMapping.rejected, (state) => {
                state.isBulkLoading = false;
            });
    }
});

export const {
    getUsers,
    getAudienceReport,
    getAudience,
    getReportsReducer,
    getAllGroupsReducer,
    getUsersByAudience,
    getReportsByUser,
    addLoading,
    addReportsLoading,
    addRolesAssignedToUser,
    addAllUsersByReport,
} = adminReducer.actions;

export default adminReducer.reducer;